"use client"

import { useState, useRef, useEffect } from "react"
import { motion, useInView, useAnimation } from "framer-motion"
import { ChevronDown } from "lucide-react"
import StockCard from "./StockCards"
import Footer from "../Footer"
import axios from "axios"

// Video imports (ensure these paths are correct in /public/Vedios)
import video1 from "/Vedios/WhatsApp Video 2025-04-29 at 1.00.52 PM.mp4"
import video2 from "/Vedios/WhatsApp Video 2025-04-29 at 1.00.28 PM.mp4"

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
}

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3,
    },
  },
}

// Updated FAQ data with generic branding
const faqs = [
  {
    question: "How does the Panjshir emerald investment work?",
    answer:
      "Choose an investment tier to stake in high-quality Panjshir emeralds. After signing an agreement, receive quarterly updates on sourcing, quality, and market trends. At cycle’s end, enjoy profits from gems, with options to reinvest or withdraw.",
  },
  {
    question: "What is the expected return on investment?",
    answer:
      "Returns vary by tier: Entry-Level (15% profit share), Mid-Level (30% profit share), and Premium-Level (50% profit share). Panjshir emeralds’ rarity and global demand drive high returns, managed transparently.",
  },
  {
    question: "How secure is my investment?",
    answer:
      "Your stake in ethically sourced Panjshir emeralds is protected with transparent reporting, secure storage, and full legal documentation, ensuring trust and safety.",
  },
  {
    question: "Can I visit the emerald operations?",
    answer:
      "Mid-Level and Premium-Level investisseurs can visit our facilities or partner mines in the Panjshir Valley, subject to logistics, to witness the source of these high-quality gems.",
  },
  {
    question: "What makes Panjshir emeralds valuable?",
    answer:
      "Panjshir emeralds are prized for their rich color, purity, and size. Sourced from Afghanistan’s Panjshir Valley, these gems command premium prices globally due to their rarity and quality.",
  },
]

export default function PricingPlans() {
  const [activeFaq, setActiveFaq] = useState(null)
  const [investmentPlans, setInvestmentPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    async function fetchPlans() {
      try {
        setLoading(true)
        const res = await axios.get("https://backend.emerald.azadnoori.com/api/v1/plans", {
          withCredentials: true, // Include cookies for authentication
        })
        setInvestmentPlans(res.data.data.Plan || []) // Fallback to empty array
        setLoading(false)
      } catch (error) {
        setError("Failed to load investment plans. Please try again later.")
        setLoading(false)
      }
    }
    fetchPlans()
  }, [])

  // Refs for scroll animations
  const heroRef = useRef(null)
  const pricingRef = useRef(null)
  const featuresRef = useRef(null)
  const faqRef = useRef(null)
  const ctaRef = useRef(null)

  // Check if elements are in view
  const heroInView = useInView(heroRef, { once: true, amount: 0.3 })
  const pricingInView = useInView(pricingRef, { once: true, amount: 0.1 })
  const featuresInView = useInView(featuresRef, { once: true, amount: 0.3 })
  const faqInView = useInView(faqRef, { once: true, amount: 0.3 })
  const ctaInView = useInView(ctaRef, { once: true, amount: 0.3 })

  // Animation controls
  const heroControls = useAnimation()
  const pricingControls = useAnimation()
  const featuresControls = useAnimation()
  const faqControls = useAnimation()
  const ctaControls = useAnimation()

  // Trigger animations when elements come into view
  useEffect(() => {
    if (heroInView) heroControls.start("visible")
    if (pricingInView) pricingControls.start("visible")
    if (featuresInView) featuresControls.start("visible")
    if (faqInView) faqControls.start("visible")
    if (ctaInView) ctaControls.start("visible")
  }, [heroInView, pricingInView, featuresInView, faqInView, ctaInView, heroControls, pricingControls, featuresControls, faqControls, ctaControls])

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index)
  }

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-gray-50 to-white font-serif overflow-x-hidden">
      {/* Hero Section */}
      <motion.section
        ref={heroRef}
        initial="hidden"
        animate={heroControls}
        variants={fadeIn}
        className="relative py-24 md:py-32 overflow-hidden"
      >
        <div className="absolute inset-0 z-0">
          <video
            src={video2}
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
            aria-label="Panjshir Emeralds hero video"
          />
          <div className="absolute inset-0 bg-black/30" />
        </div>
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-emerald-100 rounded-full opacity-20 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-emerald-200 rounded-full opacity-20 blur-3xl" />

        <div className="container relative z-10 px-4 sm:px-6 max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto">
            <motion.h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 tracking-tight text-white"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Investment Opportunities in <span className="text-emerald-400 italic">Panjshir EmeraldsVault</span>
            </motion.h1>

            <motion.p
              className="text-lg md:text-xl mb-8 text-gray-200 leading-relaxed"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              Our emerald mining business offers investors the chance to be part of a rare and profitable venture. As one of the world’s most precious gemstones, emeralds have seen consistent demand and appreciation in value. Investing in our emerald mines means partnering with a trusted source in Panjshir, Afghanistan — a region famous for its world-class emeralds.
            </motion.p>

            <motion.div
              className="text-xl md:text-2xl font-semibold text-emerald-400 italic"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              Unlock the World of Precious Gemstones
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* About Section */}
      <section className="py-16 px-4 sm:px-6">
        <div className="container mx-auto max-w-7xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">The Power of Panjshir Emeralds</h2>
              <p className="text-gray-600 mb-6">
                Panjshir emeralds are renowned for their rich color, purity, and size, making them highly sought-after in the global market. By investing in our business, you’re securing a stake in one of the most prestigious and valuable gemstones in the world.
              </p>
              <p className="text-gray-600 mb-6">
                Our emerald mining business in Panjshir, Afghanistan, offers a unique opportunity to partner with a trusted source, leveraging the region’s reputation for producing world-class emeralds with consistent demand and value appreciation.
              </p>
            </div>
            <div className="relative h-[400px] rounded-xl overflow-hidden shadow-xl border-2 border-emerald-200">
              <video
                src={video1}
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
                aria-label="Panjshir Emeralds promotional video"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Investment Plans Section */}
      <section className="py-16 px-4 sm:px-6">
        <motion.div
          ref={pricingRef}
          initial="hidden"
          animate={pricingControls}
          variants={fadeIn}
          className="container mx-auto px-4 sm:px-6 max-w-7xl"
        >
          <div className="text-center mb-16">
            <motion.div
              variants={fadeIn}
              className="inline-block px-4 py-1 bg-emerald-50 rounded-full text-emerald-800 font-sans text-sm tracking-wider uppercase mb-4"
            >
              Investment Opportunities
            </motion.div>
            <motion.h2 variants={fadeIn} className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              Our Investment Plan
            </motion.h2>
            <motion.p variants={fadeIn} className="max-w-2xl mx-auto text-gray-600 text-lg">
              We offer a structured investment plan where you can gain exclusive access to high-quality emeralds sourced directly from our mines in Panjshir, Afghanistan. This is a prime opportunity to diversify your investment portfolio and benefit from the timeless value of emeralds.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
            {loading ? (
              <p className="text-center text-gray-600 col-span-full">Loading investment plans...</p>
            ) : error ? (
              <p className="text-center text-red-600 col-span-full">{error}</p>
            ) : investmentPlans.length === 0 ? (
              <p className="text-center text-gray-600 col-span-full">No investment plans available.</p>
            ) : (
              investmentPlans.map((stock, index) => (
                <StockCard key={stock._id} stock={stock} index={index} />
              ))
            )}
          </div>
        </motion.div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 px-4 sm:px-6 bg-emerald-50">
        <motion.div
          ref={featuresRef}
          initial="hidden"
          animate={featuresControls}
          variants={fadeIn}
          className="container mx-auto max-w-7xl"
        >
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-1 bg-emerald-100 rounded-full text-emerald-800 font-sans text-sm tracking-wider uppercase mb-4">
              Process
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">How It Works</h2>
            <p className="max-w-2xl mx-auto text-gray-600 text-lg">
              Our streamlined process makes investing in Panjshir emeralds seamless, transparent, and rewarding.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-sm relative">
              <div className="absolute -top-4 -left-4 w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-lg">
                1
              </div>
              <h3 className="text-xl font-bold mb-3 pt-2 text-gray-900">Choose Your Tier</h3>
              <p className="text-gray-600">
                Select a tier to invest in rare Panjshir emeralds, tailored to your return goals.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm relative">
              <div className="absolute -top-4 -left-4 w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-lg">
                2
              </div>
              <h3 className="text-xl font-bold mb-3 pt-2 text-gray-900">Sign Agreement</h3>
              <p className="text-gray-600">
                Formalize your stake in Panjshir emeralds with a clear, secure agreement.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm relative">
              <div className="absolute -top-4 -left-4 w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-lg">
                3
              </div>
              <h3 className="text-xl font-bold mb-3 pt-2 text-gray-900">Receive Updates</h3>
              <p className="text-gray-600">
                Get quarterly insights on Panjshir emerald sourcing, quality, and market trends.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm relative">
              <div className="absolute -top-4 -left-4 w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-lg">
                4
              </div>
              <h3 className="text-xl font-bold mb-3 pt-2 text-gray-900">Profit Distribution</h3>
              <p className="text-gray-600">
                Reap profits from Panjshir emeralds, with options to reinvest or withdraw.
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 sm:px-6">
        <motion.div
          ref={faqRef}
          initial="hidden"
          animate={faqControls}
          variants={fadeIn}
          className="container mx-auto px-4 sm:px-6 max-w-4xl"
        >
          <div className="text-center mb-16">
            <motion.div
              variants={fadeIn}
              className="inline-block px-4 py-1 bg-emerald-100 rounded-full text-emerald-800 font-sans text-sm tracking-wider uppercase mb-4"
            >
              Common Questions
            </motion.div>
            <motion.h2 variants={fadeIn} className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              Frequently Asked Questions
            </motion.h2>
            <motion.p variants={fadeIn} className="max-w-2xl mx-auto text-gray-600 text-lg">
              Everything you need to know about investing in Panjshir emeralds with our emerald mining business.
            </motion.p>
          </div>

          <motion.div variants={staggerContainer} className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                variants={fadeIn}
                className="border border-emerald-100 rounded-xl overflow-hidden bg-white shadow-sm"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="flex justify-between items-center w-full p-6 text-left"
                >
                  <h3 className="font-semibold text-lg text-gray-900">{faq.question}</h3>
                  <ChevronDown
                    className={`h-5 w-5 text-emerald-600 transition-transform ${
                      activeFaq === index ? "transform rotate-180" : ""
                    }`}
                  />
                </button>
                <div
                  className={`px-6 overflow-hidden transition-all duration-300 ease-in-out ${
                    activeFaq === index ? "max-h-96 pb-6" : "max-h-0"
                  }`}
                >
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* CTA Section with Updated WhatsApp Button */}
      <section className="py-16 px-4 sm:px-6 bg-emerald-600 text-white">
        <motion.div
          ref={ctaRef}
          initial="hidden"
          animate={ctaControls}
          variants={fadeIn}
          className="container mx-auto max-w-4xl text-center"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Get in Touch
          </h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
            Ready to begin your journey into the world of emeralds? Contact us today for a personalized investment consultation. Let’s discuss how we can work together to unlock the immense value of Panjshir emeralds.
          </p>
          <div className="flex justify-center gap-4">
            <button className="bg-white text-emerald-600 hover:bg-emerald-50 px-8 py-4 rounded-lg font-bold text-lg transition-colors">
              Get Started Today
            </button>
            <a
              href="https://wa.me/+93729599599?text=I'm interested in investing in Panjshir emeralds"
              target="_blank"
              rel="noopener noreferrer"
            >
              <button className="bg-emerald-800 text-white hover:bg-emerald-900 px-8 py-4 rounded-lg font-bold text-lg transition-colors">
                Contact via WhatsApp
              </button>
            </a>
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  )
}